import React from 'react';
import BeaconProChart, {
  DefaultMenuButton
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/BeaconProChart';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { useMainEntityType } from 'src/utils/Hooks/reduxSelectors';
import {
  CardContainer,
  GaugeSummaryCard,
  GaugeSummaryCardSkeleton
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/GaugeSummaryCard';
import { useDynamicVariationsSubtitles } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/useDynamicVariationsSubtitles';
import {
  VariationsFilter,
  VariationTilesHeaders,
  VariationsGaugeData
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/helpers/contentVariationUtils';
import { VariationsGaugeAdapter } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/VariationsGaugeAdapter';
import { VariationsGaugeCardDetails } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/VariationsGaugeCardDetails';

const childVariationRateOrderMap = {
  [VariationsFilter.ChildVariationRate]: 0,
  [VariationsFilter.SingleSkuPages]: 1,
  [VariationsFilter.VariatedSkuPages]: 2
};

const childSkuAccuracyOrderMap = {
  [VariationsFilter.ChildAccuracy]: 0,
  [VariationsFilter.IncorrectChildSkus]: 1,
  [VariationsFilter.CorrectChildSkus]: 2
};

const parentPageAccuracyOrderMap = {
  [VariationsFilter.ParentPageAccuracy]: 0,
  [VariationsFilter.InaccurateParentPages]: 1,
  [VariationsFilter.PageDensity]: 2
};

interface OrderMap {
  [key: string]: number;
}

const getGroupedDataItem = (title: string, data: VariationsGaugeData[], orderMap: OrderMap) => {
  const filterKeys = Object.keys(orderMap) as VariationsFilter[];
  return {
    title,
    data: data
      .filter((item) => filterKeys.includes(item.filterKey))
      .sort((a, b) => orderMap[a.filterKey] - orderMap[b.filterKey])
  };
};

interface VariationsGaugeCardsProps {
  title: string;
  predefinedColor?: string;
  queryFn: () => Promise<VariationsGaugeAdapter>;
  renderSubtitle: () => string | React.ReactNode;
}

export const VariationsGaugeCards = (props: VariationsGaugeCardsProps) => {
  const { title, predefinedColor, queryFn, renderSubtitle } = props;
  const { generateVariationsGaugeMetricSubtitle } = useDynamicVariationsSubtitles();
  const entityType = useMainEntityType();

  return (
    <BeaconProChart queryFn={queryFn} name={`variations-gauge-card-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <BeaconProChart.Header menuButton={() => <DefaultMenuButton excludeActions={['exportCsv']} />}>
        <BeaconProChart.Title>
          <Text variant="h4">{title}</Text>
        </BeaconProChart.Title>
      </BeaconProChart.Header>
      <BeaconProChart.Subtitle render={renderSubtitle} />

      <BeaconProChart.Visualization
        render={(adapter: VariationsGaugeAdapter, isLoading: boolean) => {
          if (isLoading) {
            return (
              <Flex marginTop="32px" gap="md">
                {[...Array(3)].map((_, index) => (
                  <GaugeSummaryCardSkeleton key={index} />
                ))}
              </Flex>
            );
          }

          const fullData = adapter.transform();

          const groupedData = [
            getGroupedDataItem('Child Variation Rate', fullData.gaugeData, childVariationRateOrderMap),
            getGroupedDataItem('Child SKU Accuracy', fullData.gaugeData, childSkuAccuracyOrderMap),
            getGroupedDataItem('Parent Page Accuracy', fullData.gaugeData, parentPageAccuracyOrderMap)
          ];

          return (
            <Flex marginTop="32px" gap="md">
              {groupedData.map((group) => {
                const tileData = group.data;
                return (
                  <CardContainer key={group.title}>
                    <GaugeSummaryCard
                      key={group.title}
                      title={group.title}
                      predefinedColor={predefinedColor}
                      percentage={tileData[0].percentage}
                    />
                    {tileData.map((item, index) => {
                      const isLastItem = index === tileData.length - 1;
                      const height = index === 1 ? '108px' : '128px';
                      return (
                        <VariationsGaugeCardDetails
                          key={item.filterKey}
                          title={
                            VariationTilesHeaders[item.filterKey]?.[entityType]?.toUpperCase() ??
                            VariationTilesHeaders[item.filterKey]?.default?.toUpperCase()
                          }
                          subtitle={generateVariationsGaugeMetricSubtitle(fullData, item.filterKey)}
                          item={item}
                          height={height}
                          overrideSx={
                            isLastItem
                              ? {
                                  borderBottomLeftRadius: '8px',
                                  borderBottomRightRadius: '8px'
                                }
                              : undefined
                          }
                        />
                      );
                    })}
                  </CardContainer>
                );
              })}
            </Flex>
          );
        }}
      />
    </BeaconProChart>
  );
};
